import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { FundClassification } from '@/models/common/FundClassification';
import { FundGoals } from '@/models/common/FundGoals';
import OpenFundSection from '../components/OpenFundSection';
import { TextField } from '../components/FormField';
import { useOpenFundFieldTracking } from '../OpenFundAnalyticsContext';
import { OpenFundFormData, ResponsiblePartyOptions, type ResponsibleParty } from '../schemas/openFundFormSchema';
import fieldStyles from '../components/FormField.module.scss';
import styles from './steps.module.scss';

interface FundDefinitionStepProps {
  classification: FundClassification.Welfare | FundClassification.Community;
}

const WELFARE_GOALS: FundGoals[] = [
  FundGoals.WidowsAndOrphans,
  FundGoals.BrideHelp,
  FundGoals.Medicine,
  FundGoals.Disasters,
  FundGoals.Debts,
  FundGoals.DifficultFinancialSituation,
  FundGoals.Other,
];

const COMMUNITY_GOALS: FundGoals[] = [
  FundGoals.FamilySupport,
  FundGoals.KollelSupport,
  FundGoals.CommunityExpenses,
  FundGoals.Other,
];

const FundDefinitionStep = ({ classification }: FundDefinitionStepProps) => {
  const { t } = useTranslation();
  const { control, watch } = useFormContext<OpenFundFormData>();
  const trackField = useOpenFundFieldTracking();
  const isWelfare = classification === FundClassification.Welfare;
  const isCommunity = classification === FundClassification.Community;
  const fundGoals = (watch('fundGoals' as never) || []) as FundGoals[];
  const responsibleParty = isCommunity
    ? (watch('responsibleParty' as never) as unknown as ResponsibleParty)
    : undefined;

  const goals = isWelfare ? WELFARE_GOALS : COMMUNITY_GOALS;

  return (
    <div className={styles.stepWrapper}>
      <OpenFundSection title={t('openFundRequest.steps.fundDefinition')}>
        {isCommunity ? (
          <div className={styles.threeColGrid}>
            <TextField<OpenFundFormData>
              name="fundName"
              label={t('openFundRequest.fields.fundName')}
              placeholder={t('openFundRequest.fields.fundNamePlaceholder')}
            />
            <div className={fieldStyles.fieldWrapper}>
              <label className={`body-sm-regular ${fieldStyles.label}`}>
                {t('openFundRequest.fields.responsibleParty')}
              </label>
              <Controller
                control={control}
                name={'responsibleParty' as never}
                render={({ field }) => (
                  <select
                    value={field.value as string}
                    onFocus={() => trackField('responsibleParty')}
                    onChange={(event) => {
                      field.onChange(event);
                      trackField('responsibleParty');
                    }}
                    className={`${fieldStyles.input} body-md-regular`}
                  >
                    {ResponsiblePartyOptions.map((option) => (
                      <option key={option} value={option}>
                        {t(`openFundRequest.responsiblePartyOptions.${option}`)}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
            <TextField<OpenFundFormData>
              name={'responsibleEntityName' as never}
              label={
                responsibleParty === 'charityTrustee'
                  ? t('openFundRequest.fields.charityTrusteeName')
                  : t('openFundRequest.fields.communityName')
              }
            />
          </div>
        ) : (
          <TextField<OpenFundFormData>
            name="fundName"
            label={t('openFundRequest.fields.fundName')}
            placeholder={t('openFundRequest.fields.fundNamePlaceholder')}
          />
        )}
      </OpenFundSection>

      <OpenFundSection title={t('openFundRequest.fields.fundGoalsSection')}>
        <div className={styles.goalsWrapper}>
          <label className="body-md-semibold text-(--surface-secondary-dark)">
            {t('openFundRequest.fields.fundGoals')}
          </label>
          <Controller
            control={control}
            name={'fundGoals' as never}
            render={({ field, fieldState }) => {
              const selected = (field.value as FundGoals[]) || [];
              const toggle = (goal: FundGoals) => {
                const next = selected.includes(goal)
                  ? selected.filter((g) => g !== goal)
                  : [...selected, goal];
                field.onChange(next);
                trackField('fundGoals');
              };
              return (
                <>
                  <div className={styles.goalsPills}>
                    {goals.map((goal) => {
                      const isSelected = selected.includes(goal);
                      return (
                        <button
                          key={goal}
                          type="button"
                          className={`${styles.goalPill} ${isSelected ? styles.goalPillActive : ''}`}
                          onClick={() => toggle(goal)}
                          aria-pressed={isSelected}
                        >
                          <span
                            className={`${styles.goalPillCheck} ${isSelected ? styles.goalPillCheckActive : ''}`}
                            aria-hidden="true"
                          >
                            {isSelected && (
                              <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                  d="M1 4L3.5 6.5L9 1"
                                  stroke="white"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </span>
                          <span className="body-md-regular">
                            {t(`openFundRequest.fundGoals.${goal}`)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {fieldState.error && (
                    <span className="body-sm-regular text-(--font-error)">
                      {fieldState.error.message as string}
                    </span>
                  )}
                </>
              );
            }}
          />
          <p className={styles.helperText}>
            {t('openFundRequest.fields.fundGoalsHelperText')}
          </p>
          {fundGoals.includes(FundGoals.Other) && (
            <TextField<OpenFundFormData>
              name={'otherGoalDescription' as never}
              label={t('openFundRequest.fields.otherGoalDescription')}
            />
          )}
        </div>

        <TextField<OpenFundFormData>
          name="reasonSummary"
          label={t('openFundRequest.fields.reasonSummary')}
          multiline
          rows={3}
        />
      </OpenFundSection>
    </div>
  );
};

export default FundDefinitionStep;
